import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button, Form, Modal } from 'react-bootstrap';
import { getInverterGroup } from 'Leads/Details/utils/inverter';

const INVERTER_QUANTITY_MIN = 1;
const INVERTER_QUANTITY_MAX = 10;
const INVERTER_QUANTITY_MAX_ERROR = `Quantity cannot be more than ${INVERTER_QUANTITY_MAX}.`;

function clampInverterQuantity(raw) {
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return INVERTER_QUANTITY_MIN;
  return Math.min(
    INVERTER_QUANTITY_MAX,
    Math.max(INVERTER_QUANTITY_MIN, parsed)
  );
}

function isInverterQuantityOverMax(raw) {
  if (raw === '' || raw == null) return false;
  const parsed = parseInt(raw, 10);
  return !Number.isNaN(parsed) && parsed > INVERTER_QUANTITY_MAX;
}

function InverterQuantityErrorRow() {
  return (
    <Row className="mt-1">
      <Col xs={12}>
        <small className="text-danger text-nowrap">
          {INVERTER_QUANTITY_MAX_ERROR}
        </small>
      </Col>
    </Row>
  );
}

function SelectModal({
  show,
  title,
  selectValue,
  selectQuantity = 1,
  selectedPanels,
  panel,
  additionalInverters = [],
  selectOptions,
  nullOption,
  disabled,
  onHide,
  onSave,
  hasAutoCalculatedInverterQuantity,
  onQuantityManuallyChanged,
  onResetToAutoMode,
}) {
  const [value, setValue] = useState(selectValue);
  const [quantity, setQuantity] = useState(selectQuantity);
  const [primaryAutoQuantity, setPrimaryAutoQuantity] = useState(
    !hasAutoCalculatedInverterQuantity
  );
  const [localAdditionalInverters, setLocalAdditionalInverters] = useState(
    additionalInverters || []
  );
  const [primaryQuantityError, setPrimaryQuantityError] = useState(false);
  const [additionalQuantityErrors, setAdditionalQuantityErrors] = useState(
    {}
  );

  // Check if selected inverter is microinverter for auto-disabling quantity
  const selectedInverter = selectOptions.find(opt => opt.id === value);
  const isMicroInverter = getInverterGroup(selectedInverter) === 'micro';
  const quantityDisabled = disabled || isMicroInverter || primaryAutoQuantity;
  const handleDismiss = () => {
    onHide();
  };
  const persistInverterAndClose = () => {
    const additionalToSave = isMicroInverter ? [] : localAdditionalInverters;
    onSave(value, quantity, additionalToSave, primaryAutoQuantity);
    onHide();
  };
  const updateAdditionalInverter = (index, field, newValue) => {
    const updated = [...localAdditionalInverters];
    updated[index] = { ...updated[index], [field]: newValue };
    setLocalAdditionalInverters(updated);
  };
  const addAdditionalInverter = () => {
    setLocalAdditionalInverters([
      ...localAdditionalInverters,
      { inverter_id: '', quantity: 1 },
    ]);
  };
  const removeAdditionalInverter = (index) => {
    const updated = localAdditionalInverters.filter((_, i) => i !== index);
    setLocalAdditionalInverters(updated);
    setAdditionalQuantityErrors({});
  };
  useEffect(() => {
    if (!show) return;
    setValue(selectValue);
    setQuantity(clampInverterQuantity(selectQuantity));
    setLocalAdditionalInverters(additionalInverters || []);
    setPrimaryQuantityError(false);
    setAdditionalQuantityErrors({});
    setPrimaryAutoQuantity(!hasAutoCalculatedInverterQuantity);
  }, [show, selectValue, selectQuantity, JSON.stringify(additionalInverters)]);

  useEffect(() => {
    if (!show) return;
    setPrimaryAutoQuantity(!hasAutoCalculatedInverterQuantity);
  }, [show, hasAutoCalculatedInverterQuantity]);

  useEffect(() => {
    if (!value) return;
    const primaryOpt = selectOptions.find((opt) => opt.id === value);
    if (!primaryOpt) return;
    const primaryGroup = getInverterGroup(primaryOpt);
    const next = (localAdditionalInverters || []).filter((ai) => {
      if (!ai?.inverter_id) return true;
      const opt = selectOptions.find((o) => o.id === ai.inverter_id);
      if (!opt) return false;
      return getInverterGroup(opt) === primaryGroup;
    });
    if (next.length !== (localAdditionalInverters || []).length) {
      setLocalAdditionalInverters(next);
    }
  }, [value, JSON.stringify(selectOptions)]);

  return (
    <Modal
      size="md"
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      onHide={handleDismiss}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {title === 'Inverter' ? (
          <>
            <Row className="mt-2">
              <Col sm={5} className="h6">
                <small className="mb-1" style={{ fontWeight: 600 }}>
                  Primary Inverter:
                </small>
                <Form.Control
                  as="select"
                  onChange={(e) => setValue(e.target.value)}
                  value={value}
                  disabled={disabled}
                >
                  {nullOption ? (
                    <option value="">{nullOption}</option>
                  ) : (
                    <option disabled>{`Select ${title}`}</option>
                  )}
                  {selectOptions
                    .filter((el) => {
                      const selectedAdditionalIds = localAdditionalInverters.map(ai => ai.inverter_id);
                      return !selectedAdditionalIds.includes(el.id);
                    })
                    .map(({ id, label }) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                </Form.Control>
              </Col>
              {!isMicroInverter && (
                <Col sm={1} className="h6 d-flex align-items-center justify-content-center">
                  <Form.Check
                    type="checkbox"
                    checked={!primaryAutoQuantity}
                    onChange={(e) => {
                      const manual = e.target.checked;
                      const nextAuto = !manual;
                      setPrimaryAutoQuantity(nextAuto);
                      if (nextAuto) setPrimaryQuantityError(false);
                      if (manual && onQuantityManuallyChanged) onQuantityManuallyChanged();
                      if (nextAuto && onResetToAutoMode) onResetToAutoMode();
                      if (manual && quantity < 1) setQuantity(1);
                    }}
                    title="Manual quantity"
                  />
                </Col>
              )}
              <Col sm={isMicroInverter ? 3 : 2} className="h6">
                <small
                  className="mb-1 text-nowrap d-inline-block"
                  style={{ fontWeight: 600 }}
                >
                  {isMicroInverter || primaryAutoQuantity ? 'Qty (Auto):' : 'Qty:'}
                </small>
                <Form.Control
                  type={primaryAutoQuantity ? "text" : "number"}
                  min="1"
                  max="10"
                  value={primaryAutoQuantity ? "Auto" : quantity}
                  onChange={(e) => {
                    setPrimaryQuantityError(
                      isInverterQuantityOverMax(e.target.value)
                    );
                    const newQuantity = clampInverterQuantity(e.target.value);
                    setQuantity(newQuantity);
                    if (onQuantityManuallyChanged && !isMicroInverter) {
                      onQuantityManuallyChanged();
                    }
                  }}
                  isInvalid={primaryQuantityError}
                  disabled={quantityDisabled}
                  style={quantityDisabled ? { backgroundColor: '#f8f9fa', color: '#6c757d', minWidth: "150px", maxWidth: "150px", width: "150px" } : { minWidth: "80px", maxWidth: "200px", width: "80px" }}
                />
              </Col>
            </Row>
            {primaryQuantityError && <InverterQuantityErrorRow />}
            {(isMicroInverter || primaryAutoQuantity) && (
              <Row className="mt-2">
                <Col>
                  <small className="text-muted">
                    Solar-production service controls auto quantity while this mode is enabled.
                  </small>
                </Col>
              </Row>
            )}
            {!isMicroInverter && (
              <>
                {localAdditionalInverters.map((inverter, index) => (
                  <React.Fragment key={index}>
                    <Row className="mt-3">
                      <Col sm={5} className="h6">
                        <small className="mb-1" style={{ fontWeight: 600 }}>
                          Additional Inverter {index + 1}:
                        </small>
                        <Form.Control
                          as="select"
                          onChange={(e) =>
                            updateAdditionalInverter(
                              index,
                              'inverter_id',
                              e.target.value
                            )
                          }
                          value={inverter.inverter_id || ''}
                          disabled={disabled}
                        >
                          <option value="">Select Inverter</option>
                          {selectOptions
                            .filter((el) => {
                              const primaryOpt = selectOptions.find(
                                (opt) => opt.id === value
                              );
                              if (primaryOpt) {
                                if (
                                  getInverterGroup(el) !==
                                  getInverterGroup(primaryOpt)
                                )
                                  return false;
                              }

                              if (el.id === value) return false;

                              const otherSelectedIds = localAdditionalInverters
                                .filter((_, i) => i !== index)
                                .map((ai) => ai.inverter_id);
                              return !otherSelectedIds.includes(el.id);
                            })
                            .map(({ id, label }) => (
                              <option key={id} value={id}>
                                {label}
                              </option>
                            ))}
                        </Form.Control>
                      </Col>
                      <Col
                        sm={1}
                        className="h6 d-flex align-items-end justify-content-center"
                      >
                        <Form.Check
                          type="checkbox"
                          checked={inverter.autoQuantity === false}
                          onChange={(e) => {
                            const manual = e.target.checked;
                            updateAdditionalInverter(
                              index,
                              'autoQuantity',
                              manual ? false : true
                            );
                            if (!manual) {
                              setAdditionalQuantityErrors((prev) => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }
                          }}
                          title="Manual quantity"
                          disabled={disabled}
                        />
                      </Col>
                      <Col sm={4} className="h6">
                        <small
                          className="mb-1 text-nowrap d-inline-block"
                          style={{ fontWeight: 600 }}
                        >
                          {inverter.autoQuantity !== false
                            ? 'Qty (Auto):'
                            : 'Qty:'}
                        </small>
                        <Form.Control
                          type={
                            inverter.autoQuantity !== false ? 'text' : 'number'
                          }
                          min="1"
                          max="10"
                          value={
                            inverter.autoQuantity !== false
                              ? 'Auto'
                              : inverter.quantity || 1
                          }
                          onChange={(e) => {
                            setAdditionalQuantityErrors((prev) => ({
                              ...prev,
                              [index]: isInverterQuantityOverMax(e.target.value),
                            }));
                            updateAdditionalInverter(
                              index,
                              'quantity',
                              clampInverterQuantity(e.target.value)
                            );
                          }}
                          isInvalid={Boolean(additionalQuantityErrors[index])}
                          disabled={disabled || inverter.autoQuantity !== false}
                        />
                      </Col>
                      <Col sm={1} className="h6 d-flex align-items-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeAdditionalInverter(index)}
                        >
                          ×
                        </Button>
                      </Col>
                    </Row>
                    {additionalQuantityErrors[index] && (
                      <InverterQuantityErrorRow />
                    )}
                  </React.Fragment>
                ))}
                <Row className="mt-2">
                  <Col>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addAdditionalInverter}
                      disabled={disabled}
                    >
                      + Add Another Inverter
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </>
        ) : (
          <Row className="mt-2">
            <Col sm={8} className="h6">
              <Form.Control
                as="select"
                onChange={(e) => setValue(e.target.value)}
                value={value}
                disabled={disabled}
              >
                {nullOption ? (
                  <option value="">{nullOption}</option>
                ) : (
                  <option disabled>{`Select ${title}`}</option>
                )}
                {selectOptions.map(({ id, label }) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleDismiss('cancel_button')}
        >
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={persistInverterAndClose}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

SelectModal.defaultProps = {
  nullOption: null,
  disabled: false,
};

SelectModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  selectValue: PropTypes.string.isRequired,
  selectedPanels: PropTypes.object,
  panel: PropTypes.object,
  hasAutoCalculatedInverterQuantity: PropTypes.bool,
  onQuantityManuallyChanged: PropTypes.func,
  onResetToAutoMode: PropTypes.func,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
      capacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  nullOption: PropTypes.string,
  disabled: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default SelectModal;
